<script setup lang="ts">
/**
 * ScrollReveal — fades/slides its content in the first time it scrolls into view.
 *
 * SSR-safe: the server render and the first client render are VISIBLE, so the static
 * HTML never hides content (the site is prerendered — hidden above-the-fold text/images
 * used to be invisible until hydration, hurting LCP) and hydration matches. On mount,
 * anything already on screen simply stays (no entrance); anything below the fold is
 * hidden — out of sight, so no flash — and revealed when it scrolls in.
 */
import type { PrimitiveProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { Primitive } from 'reka-ui'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { unrefElement } from '@vueuse/core'
import { cn } from '@/lib/utils'

interface Props extends PrimitiveProps {
  threshold?: number
  rootMargin?: string
  triggerOnce?: boolean
  delay?: number
  class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  as: 'div',
  threshold: 0.1,
  rootMargin: '0px 0px 100px 0px',
  triggerOnce: true,
  delay: 0,
})

const targetRef = ref<HTMLElement | null>(null)
const visible = ref(true)
// Only an element that was hidden on mount gets the entrance transition (and its delay).
const armed = ref(false)
let observer: IntersectionObserver | null = null

onMounted(() => {
  const el = unrefElement(targetRef) as HTMLElement | null | undefined
  if (!el || typeof IntersectionObserver === 'undefined') return
  const rect = el.getBoundingClientRect()
  if (rect.top < window.innerHeight && rect.bottom > 0) return // on screen already: stays put
  visible.value = false
  armed.value = true
  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        visible.value = true
        if (props.triggerOnce) observer?.disconnect()
      } else if (!props.triggerOnce) {
        visible.value = false
      }
    },
    { threshold: props.threshold, rootMargin: props.rootMargin },
  )
  observer.observe(el)
})
onBeforeUnmount(() => observer?.disconnect())

const visibilityClass = computed(() => {
  if (!visible.value) return 'motion-hidden'
  return armed.value ? 'motion-visible is-armed' : 'motion-visible'
})
</script>

<template>
  <Primitive
    ref="targetRef"
    :as="as"
    :as-child="asChild"
    :class="cn(visibilityClass, props.class)"
    :style="armed && visible && delay ? { transitionDelay: `${delay}ms` } : undefined"
  >
    <slot />
  </Primitive>
</template>

<style scoped>
.motion-hidden {
  opacity: 0;
  transform: translateY(20px);
}

.motion-visible.is-armed {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .motion-hidden {
    opacity: 1;
    transform: none;
  }
  .motion-visible.is-armed {
    transition: none;
  }
}
</style>
